import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { runtimeConfig } from "@/shared/config/runtime-config";

const endpoint = (path: string): string | URL =>
  runtimeConfig.apiBaseUrl ? new URL(path, runtimeConfig.apiBaseUrl) : path;

const currentCustomerSchema = z.object({
  id: z.uuid(),
  email: z.email(),
  displayName: z.string().min(1),
  pets: z.array(
    z.object({
      id: z.uuid(),
      name: z.string().min(1),
      legacyPetTypeId: z.string().min(1),
      petTypeKey: z.string().min(1).optional(),
      recommendedDailyGrams: z.number().int().positive().optional(),
    }),
  ),
});

export type CurrentV3Customer = z.infer<typeof currentCustomerSchema>;

export class CustomerProfilePendingError extends Error {
  public constructor() {
    super("Your PetChef profile is still being prepared");
    this.name = "CustomerProfilePendingError";
  }
}

export async function getCurrentV3Customer(signal: AbortSignal): Promise<CurrentV3Customer> {
  const response = await fetch(endpoint("/v3/me"), {
    headers: { Accept: "application/json" },
    credentials: "include",
    signal,
  });
  if (response.status === 404) throw new CustomerProfilePendingError();
  if (!response.ok) throw new Error("Could not load your customer profile");
  return currentCustomerSchema.parse(await response.json());
}

export function useCurrentV3Customer() {
  return useQuery({
    queryKey: ["current-v3-customer"],
    queryFn: ({ signal }) => getCurrentV3Customer(signal),
    staleTime: 60_000,
    retry: (failureCount, error) =>
      error instanceof CustomerProfilePendingError && failureCount < 30,
    retryDelay: 1_000,
  });
}

export async function hasNativeV3Session(): Promise<boolean> {
  try {
    const response = await fetch(endpoint("/v3/auth/session"), {
      headers: { Accept: "application/json" },
      credentials: "include",
    });
    return response.ok;
  } catch {
    return false;
  }
}
